import type { Project, TagColorMap } from '@portfolio-pm/shared';
import { Check, Plus } from 'lucide-react';
import {
  type CSSProperties,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  type DateToolbarField,
  POSTPONE_OPTIONS,
  type PostponeOptionId,
  type TaskPatch,
} from './options.ts';
import {
  buildDatePatch,
  buildDateQuickOptions,
  buildSmartDatePatch,
  getTagColor,
  tagsFromSearchText,
  uniqueTags,
} from './task-actions.ts';

/**
 * Task toolbar building blocks: the icon button, custom glyph set,
 * shared popover positioning hook, and the field menus (generic
 * options, date, postpone, project, tags). Extracted from
 * TaskWorkspace.tsx.
 */

export function IconToolbarButton({
  label,
  disabled = false,
  active = false,
  subtle = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  active?: boolean;
  subtle?: boolean;
  onClick(): void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`ui-button toolbar-icon-button ${
        active ? 'ui-button-active' : subtle ? 'ui-button-subtle' : ''
      }`}
    >
      {children}
    </button>
  );
}

export type ToolbarGlyphName =
  | 'arrangement'
  | 'complete'
  | 'completion-both'
  | 'completion-done'
  | 'completion-open'
  | 'daypart'
  | 'display'
  | 'due'
  | 'estimate'
  | 'location'
  | 'more'
  | 'note'
  | 'owner'
  | 'postpone'
  | 'priority'
  | 'project'
  | 'repeat'
  | 'restore'
  | 'row-date'
  | 'scheduled'
  | 'select'
  | 'subtasks'
  | 'tags'
  | 'workflow';

export function ToolbarGlyph({
  name,
  'data-testid': testId,
}: {
  name: ToolbarGlyphName;
  'data-testid'?: string;
}): JSX.Element {
  return (
    <svg
      data-testid={testId}
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="toolbar-glyph"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {renderToolbarGlyph(name)}
    </svg>
  );
}

function renderToolbarGlyph(name: ToolbarGlyphName): JSX.Element {
  switch (name) {
    case 'arrangement':
      return (
        <>
          <path d="M5 7h14M5 12h14M5 17h14" />
          <circle cx="9" cy="7" r="1.7" fill="currentColor" stroke="none" />
          <circle cx="15" cy="12" r="1.7" fill="currentColor" stroke="none" />
          <circle cx="11" cy="17" r="1.7" fill="currentColor" stroke="none" />
        </>
      );
    case 'complete':
      return (
        <>
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="m8 12.4 2.5 2.5L16.5 9" />
        </>
      );
    case 'completion-both':
      return (
        <>
          <circle cx="8" cy="8" r="3" />
          <circle cx="16" cy="16" r="3" />
          <path d="m14.3 15.8 1.1 1.1 2-2.2" />
        </>
      );
    case 'completion-done':
      return (
        <>
          <circle cx="12" cy="12" r="7" />
          <path d="m8.7 12.2 2.1 2.1 4.5-5" />
        </>
      );
    case 'completion-open':
      return (
        <>
          <circle cx="12" cy="12" r="7" />
          <path d="M12 8v8" />
        </>
      );
    case 'display':
      return (
        <>
          <rect x="4" y="5" width="7" height="6" rx="1.4" />
          <rect x="13" y="5" width="7" height="6" rx="1.4" />
          <path d="M4 15h16M4 19h11" />
        </>
      );
    case 'daypart':
      return (
        <>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
        </>
      );
    case 'due':
      return (
        <>
          <rect x="5" y="5" width="14" height="14" rx="2" />
          <path d="M8 3v4M16 3v4M5 10h14" />
          <circle cx="15.5" cy="15.5" r="1.7" fill="currentColor" stroke="none" />
        </>
      );
    case 'estimate':
      return (
        <>
          <circle cx="12" cy="12" r="7" />
          <path d="M12 8v4l2.8 2M9 3h6" />
        </>
      );
    case 'location':
      return (
        <>
          <path d="M12 21s6-5.4 6-11a6 6 0 1 0-12 0c0 5.6 6 11 6 11Z" />
          <circle cx="12" cy="10" r="2" />
        </>
      );
    case 'more':
      return (
        <>
          <circle cx="6.5" cy="12" r="1.8" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none" />
          <circle cx="17.5" cy="12" r="1.8" fill="currentColor" stroke="none" />
        </>
      );
    case 'note':
      return (
        <>
          <path d="M6 4h10l2 2v14H6Z" />
          <path d="M15 4v4h4M9 12h6M9 16h5" />
        </>
      );
    case 'owner':
      return (
        <>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5.5 20a6.5 4.8 0 0 1 13 0" />
        </>
      );
    case 'postpone':
      return (
        <>
          <rect x="4" y="5" width="13" height="13" rx="2" />
          <path d="M7 3v4M14 3v4M4 10h13M14 14h6M17 11l3 3-3 3" />
        </>
      );
    case 'priority':
      return (
        <>
          <path d="M6 20V5M7 5h10l-2 4 2 4H7" />
          <path d="M10 8h3M10 11h2" />
        </>
      );
    case 'project':
      return (
        <>
          <path d="M4 8.5h6l1.5 2H20v7.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
          <path d="M14 15h5M16.5 12.5 19 15l-2.5 2.5" />
        </>
      );
    case 'repeat':
      return (
        <>
          <path d="M17 3l3 3-3 3" />
          <path d="M4 11V9a3 3 0 0 1 3-3h13" />
          <path d="M7 21l-3-3 3-3" />
          <path d="M20 13v2a3 3 0 0 1-3 3H4" />
        </>
      );
    case 'restore':
      return (
        <>
          <rect x="5" y="6" width="14" height="14" rx="3" />
          <path d="M9 12h5a3 3 0 1 1 0 6h-1" />
          <path d="m9 12 3-3M9 12l3 3" />
        </>
      );
    case 'row-date':
      return (
        <>
          <path d="M5 7h14M5 12h8M5 17h5" />
          <rect x="14" y="11" width="6" height="7" rx="1.2" />
          <path d="M16 9v3M18 9v3" />
        </>
      );
    case 'scheduled':
      return (
        <>
          <rect x="5" y="5" width="14" height="14" rx="2" />
          <path d="M8 3v4M16 3v4M5 10h14" />
          <path d="M12 14.5h3.5v2.7" />
        </>
      );
    case 'select':
      return (
        <>
          <rect x="4" y="5" width="5" height="5" rx="1" />
          <rect x="4" y="14" width="5" height="5" rx="1" />
          <path d="M12 7.5h8M12 16.5h8" />
        </>
      );
    case 'subtasks':
      return (
        <>
          <rect x="4" y="4" width="6" height="5" rx="1.2" />
          <path d="M7 9v4h4" />
          <rect x="13" y="11" width="7" height="4" rx="1.1" />
          <rect x="13" y="17" width="7" height="4" rx="1.1" />
        </>
      );
    case 'tags':
      return (
        <>
          <path d="M9.2 5.5 8 18.5M16 5.5l-1.2 13M5.5 9.2h13M5 14.8h13" />
        </>
      );
    case 'workflow':
      return (
        <>
          <rect x="4" y="5" width="4" height="14" rx="1.2" />
          <rect x="10" y="5" width="4" height="14" rx="1.2" />
          <rect x="16" y="5" width="4" height="14" rx="1.2" />
          <circle cx="12" cy="9" r="1.2" fill="currentColor" stroke="none" />
        </>
      );
  }
}

/** Fixed-position popover placement below a toolbar trigger, with outside-click close. */
export function useToolbarPopoverPosition({
  align = 'left',
  menuRef,
  onClose,
  open,
  triggerRef,
  width,
}: {
  align?: 'left' | 'right';
  menuRef: RefObject<HTMLElement>;
  onClose(): void;
  open: boolean;
  triggerRef: RefObject<HTMLElement>;
  width: number;
}): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({
    left: 0,
    top: 0,
    visibility: 'hidden',
    width,
  });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!open || !trigger || typeof window === 'undefined') return;

    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 8;
    const menuWidth = Math.min(width, window.innerWidth - viewportPadding * 2);
    const desiredLeft = align === 'right' ? rect.right - menuWidth : rect.left;
    const left = Math.min(
      Math.max(desiredLeft, viewportPadding),
      window.innerWidth - menuWidth - viewportPadding,
    );
    setStyle({
      left,
      top: rect.bottom + 8,
      visibility: 'visible',
      width: menuWidth,
    });
  }, [align, open, triggerRef, width]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [menuRef, onClose, open, triggerRef]);

  return style;
}

export function ToolbarIconMenu({
  'data-testid': testId,
  label,
  open,
  align = 'left',
  options,
  activeId,
  onToggle,
  onClose,
  onSelect,
  children,
}: {
  'data-testid': string;
  label: string;
  open: boolean;
  align?: 'left' | 'right';
  options: Array<{ id: string; label: string; disabled?: boolean }>;
  activeId?: string;
  onToggle(): void;
  onClose(): void;
  onSelect(optionId: string): void;
  children: JSX.Element;
}): JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const style = useToolbarPopoverPosition({
    align,
    menuRef,
    onClose,
    open,
    triggerRef,
    width: 192,
  });
  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            data-testid={`${testId}-menu`}
            style={style}
            className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
          >
            <p className="eyebrow-label px-2 py-1">{label}</p>
            <div className="space-y-1">
              {options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  data-testid={`${testId}-option-${option.id}`}
                  disabled={option.disabled}
                  onClick={() => onSelect(option.id)}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm transition ${
                    option.disabled
                      ? 'cursor-not-allowed text-[color:var(--app-text-soft)] opacity-60'
                      : 'text-[color:var(--app-text-muted)] hover:bg-white/70 hover:text-[color:var(--app-text)]'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {activeId === option.id && <Check aria-hidden="true" size={14} />}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={onToggle}
        className="ui-button toolbar-icon-button"
      >
        {children}
      </button>
      {menu}
    </div>
  );
}

export function DateToolbarMenu({
  'data-testid': testId,
  label,
  field,
  now,
  open,
  onToggle,
  onClose,
  onPatch,
  children,
}: {
  'data-testid': string;
  label: string;
  field: DateToolbarField;
  now: number;
  open: boolean;
  onToggle(): void;
  onClose(): void;
  onPatch(patch: TaskPatch): void;
  children: JSX.Element;
}): JSX.Element {
  const [textValue, setTextValue] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const quickOptions = useMemo(() => buildDateQuickOptions(field, now), [field, now]);
  const style = useToolbarPopoverPosition({
    align: 'left',
    menuRef,
    onClose,
    open,
    triggerRef,
    width: 256,
  });

  const submitText = (): void => {
    const patch = buildSmartDatePatch(field, textValue, now);
    if (Object.keys(patch).length === 0) return;
    onPatch(patch);
    setTextValue('');
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            data-testid={`${testId}-menu`}
            style={style}
            className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
          >
            <p className="eyebrow-label px-2 py-1">{label}</p>
            <label className="sr-only" htmlFor={`${testId}-text`}>
              Smart date entry
            </label>
            <div className="flex gap-2 p-2">
              <input
                id={`${testId}-text`}
                data-testid={`${testId}-text`}
                value={textValue}
                onChange={(event) => setTextValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  submitText();
                }}
                placeholder="Date or text"
                className="ui-input h-9 min-w-0 flex-1 py-1.5 text-xs"
              />
              <button
                type="button"
                data-testid={`${testId}-text-apply`}
                onClick={submitText}
                className="ui-button h-9 px-3 text-xs"
              >
                Apply
              </button>
            </div>
            <div className="space-y-1 border-t border-[color:var(--app-border)] px-1 pt-2">
              {quickOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  data-testid={`${testId}-option-${option.id}`}
                  onClick={() => onPatch(option.patch)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-[color:var(--app-text-muted)] transition hover:bg-white/70 hover:text-[color:var(--app-text)]"
                >
                  <span className="truncate">{option.label}</span>
                </button>
              ))}
            </div>
            <label className="mt-2 grid gap-1 border-t border-[color:var(--app-border)] px-2 pt-2 text-xs font-medium text-[color:var(--app-text-muted)]">
              Calendar
              <input
                data-testid={`${testId}-calendar`}
                type="date"
                onChange={(event) => {
                  onPatch(buildDatePatch(field, event.target.value));
                  event.target.value = '';
                }}
                className="ui-input h-9 py-1.5 text-xs"
              />
            </label>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={onToggle}
        className="ui-button toolbar-icon-button"
      >
        {children}
      </button>
      {menu}
    </div>
  );
}

export function PostponeToolbarMenu({
  'data-testid': testId,
  label,
  open,
  onToggle,
  onClose,
  onQuickSelect,
  onSmartSubmit,
  children,
}: {
  'data-testid': string;
  label: string;
  open: boolean;
  onToggle(): void;
  onClose(): void;
  onQuickSelect(optionId: PostponeOptionId): void;
  onSmartSubmit(value: string): boolean;
  children: JSX.Element;
}): JSX.Element {
  const [textValue, setTextValue] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const style = useToolbarPopoverPosition({
    align: 'left',
    menuRef,
    onClose,
    open,
    triggerRef,
    width: 256,
  });

  const submitText = (): void => {
    const applied = onSmartSubmit(textValue);
    if (!applied) return;
    setTextValue('');
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            data-testid={`${testId}-menu`}
            style={style}
            className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
          >
            <p className="eyebrow-label px-2 py-1">{label}</p>
            <label className="sr-only" htmlFor={`${testId}-text`}>
              Smart postpone entry
            </label>
            <div className="flex gap-2 p-2">
              <input
                id={`${testId}-text`}
                data-testid={`${testId}-text`}
                value={textValue}
                onChange={(event) => setTextValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  submitText();
                }}
                placeholder="Date or text"
                className="ui-input h-9 min-w-0 flex-1 py-1.5 text-xs"
              />
              <button
                type="button"
                data-testid={`${testId}-text-apply`}
                onClick={submitText}
                className="ui-button h-9 px-3 text-xs"
              >
                Apply
              </button>
            </div>
            <div className="space-y-1 border-t border-[color:var(--app-border)] px-1 pt-2">
              {POSTPONE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  data-testid={`${testId}-option-${option.id}`}
                  onClick={() => onQuickSelect(option.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-[color:var(--app-text-muted)] transition hover:bg-white/70 hover:text-[color:var(--app-text)]"
                >
                  <span className="truncate">{option.label}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={onToggle}
        className="ui-button toolbar-icon-button"
      >
        {children}
      </button>
      {menu}
    </div>
  );
}

export function ProjectToolbarMenu({
  'data-testid': testId,
  label,
  open,
  projects,
  onToggle,
  onClose,
  onSelect,
  children,
}: {
  'data-testid': string;
  label: string;
  open: boolean;
  projects: Project[];
  onToggle(): void;
  onClose(): void;
  onSelect(projectId: string): void;
  children: JSX.Element;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const style = useToolbarPopoverPosition({
    align: 'left',
    menuRef,
    onClose,
    open,
    triggerRef,
    width: 256,
  });
  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return projects;
    return projects.filter((project) => project.name.toLowerCase().includes(normalizedQuery));
  }, [projects, query]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            data-testid={`${testId}-menu`}
            style={style}
            className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
          >
            <p className="eyebrow-label px-2 py-1">{label}</p>
            <label className="sr-only" htmlFor={`${testId}-search`}>
              Search projects
            </label>
            <input
              id={`${testId}-search`}
              data-testid={`${testId}-search`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search projects"
              className="ui-input my-2 h-9 w-full py-1.5 text-xs"
            />
            <div
              data-testid={`${testId}-list`}
              className="max-h-[13.5rem] space-y-1 overflow-y-auto pr-1"
            >
              {filteredProjects.length === 0 ? (
                <p className="surface-empty p-3 text-xs">No matching projects.</p>
              ) : (
                filteredProjects.map((project) => (
                  <button
                    key={project.id}
                    type="button"
                    data-testid={`${testId}-option-${project.id}`}
                    onClick={() => onSelect(project.id)}
                    className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-[color:var(--app-text-muted)] transition hover:bg-white/70 hover:text-[color:var(--app-text)]"
                  >
                    <span className="truncate">{project.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={onToggle}
        className="ui-button toolbar-icon-button"
      >
        {children}
      </button>
      {menu}
    </div>
  );
}

export function TagsToolbarMenu({
  'data-testid': testId,
  label,
  open,
  existingTags,
  initialTags,
  tagColors,
  onToggle,
  onClose,
  onApply,
  children,
}: {
  'data-testid': string;
  label: string;
  open: boolean;
  existingTags: Array<{ tag: string; count: number }>;
  initialTags: string[];
  tagColors: TagColorMap;
  onToggle(): void;
  onClose(): void;
  onApply(tags: string[]): void;
  children: JSX.Element;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [draftTags, setDraftTags] = useState<string[]>(initialTags);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const style = useToolbarPopoverPosition({
    align: 'right',
    menuRef,
    onClose,
    open,
    triggerRef,
    width: 288,
  });
  const normalizedQuery = query.trim().toLowerCase();
  const selectedTagSet = useMemo(() => new Set(draftTags), [draftTags]);
  const visibleTags = useMemo(() => {
    const tagsByName = new Map(existingTags.map((entry) => [entry.tag, entry]));
    for (const tag of draftTags) {
      if (!tagsByName.has(tag)) tagsByName.set(tag, { tag, count: 0 });
    }
    return [...tagsByName.values()]
      .filter(({ tag }) => !normalizedQuery || tag.toLowerCase().includes(normalizedQuery))
      .sort((left, right) => {
        const leftSelected = selectedTagSet.has(left.tag);
        const rightSelected = selectedTagSet.has(right.tag);
        if (leftSelected !== rightSelected) return leftSelected ? -1 : 1;
        return left.tag.localeCompare(right.tag, undefined, { sensitivity: 'base' });
      });
  }, [draftTags, existingTags, normalizedQuery, selectedTagSet]);
  const addableTags = useMemo(
    () => tagsFromSearchText(query).filter((tag) => !selectedTagSet.has(tag)),
    [query, selectedTagSet],
  );

  useEffect(() => {
    if (!open) return;
    setDraftTags(initialTags);
    setQuery('');
  }, [initialTags, open]);

  const toggleTag = (tag: string): void => {
    setDraftTags((current) =>
      current.includes(tag) ? current.filter((candidate) => candidate !== tag) : [...current, tag],
    );
  };

  const addTagsFromQuery = (): void => {
    if (addableTags.length === 0) return;
    setDraftTags((current) => uniqueTags([...current, ...addableTags]));
    setQuery('');
  };

  const applyTags = (): void => {
    onApply(
      [...draftTags].sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: 'base' }),
      ),
    );
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            data-testid={`${testId}-menu`}
            style={style}
            className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
          >
            <p className="eyebrow-label px-2 py-1">{label}</p>
            <label className="sr-only" htmlFor={`${testId}-search`}>
              Search tags
            </label>
            <input
              id={`${testId}-search`}
              data-testid={`${testId}-search`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addTagsFromQuery();
              }}
              placeholder="Search or add tags"
              className="ui-input my-2 h-9 w-full py-1.5 text-xs"
            />
            {addableTags.length > 0 && (
              <button
                type="button"
                data-testid={`${testId}-add`}
                onClick={addTagsFromQuery}
                className="mb-2 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-[color:var(--app-text-muted)] transition hover:bg-white/70 hover:text-[color:var(--app-text)]"
              >
                <Plus aria-hidden="true" size={14} />
                <span className="truncate">
                  Add {addableTags.map((tag) => `#${tag}`).join(', ')}
                </span>
              </button>
            )}
            <div
              data-testid={`${testId}-list`}
              className="max-h-60 space-y-1 overflow-y-auto border-t border-[color:var(--app-border)] pt-2"
            >
              {visibleTags.length === 0 ? (
                <p className="surface-empty p-3 text-xs">No matching tags.</p>
              ) : (
                visibleTags.map(({ tag, count }) => {
                  const selected = selectedTagSet.has(tag);
                  const tagColor = getTagColor(tag, tagColors);
                  const selectedStyle = selected
                    ? {
                        borderColor: `${tagColor}66`,
                        backgroundColor: `${tagColor}1f`,
                        color: tagColor,
                      }
                    : undefined;
                  return (
                    <label
                      key={tag}
                      data-testid={`${testId}-tag-${tag}`}
                      style={selectedStyle}
                      className={`flex cursor-pointer items-center justify-between gap-2 rounded-md border px-2 py-2 text-sm transition hover:bg-white/70 ${
                        selected
                          ? ''
                          : 'border-transparent text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text)]'
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <input
                          type="checkbox"
                          data-testid={`${testId}-option-${tag}`}
                          checked={selected}
                          onChange={() => toggleTag(tag)}
                          style={{ accentColor: tagColor }}
                          className="h-4 w-4"
                        />
                        <span className="truncate">#{tag}</span>
                      </span>
                      <span className="ui-chip px-2 py-0.5 text-[11px]">{count}</span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="mt-2 flex justify-end gap-2 border-t border-[color:var(--app-border)] pt-2">
              <button
                type="button"
                data-testid={`${testId}-clear`}
                onClick={() => setDraftTags([])}
                className="ui-button ui-button-subtle h-9 px-3 text-xs"
              >
                Clear
              </button>
              <button
                type="button"
                data-testid={`${testId}-apply`}
                onClick={applyTags}
                className="ui-button ui-button-primary h-9 px-3 text-xs"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={label}
        title={label}
        aria-expanded={open}
        onClick={onToggle}
        className="ui-button toolbar-icon-button"
      >
        {children}
      </button>
      {menu}
    </div>
  );
}
